import { useEffect, useState, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../providers/AuthProvider'
import { REGIONS } from '../components/shared/regions'
import RegionPickerModal from '../components/shared/RegionPickerModal'

export default function PharmacistCalendarPage() {
    const { user } = useAuth()

    const [jobs, setJobs] = useState([])
    const [jobsLoading, setJobsLoading] = useState(false)

    // Filter State
    const [selectedRegion, setSelectedRegion] = useState('') // Default empty (All)
    const [selectedDistrict, setSelectedDistrict] = useState('')
    const [selectedWorkType, setSelectedWorkType] = useState('모든 근무 유형')
    const [isRegionModalOpen, setIsRegionModalOpen] = useState(false)

    const availableDistricts = useMemo(() => {
        if (!selectedRegion) return [] // '모든 지역' case handled by empty string now
        return REGIONS[selectedRegion] || []
    }, [selectedRegion])

    // Calendar State
    const [currentMonth, setCurrentMonth] = useState(() => {
        const now = new Date()
        return new Date(now.getFullYear(), now.getMonth(), 1)
    })

    const loadOpenJobs = useCallback(async () => {
        setJobsLoading(true)
        try {
            const { data, error: jobsError } = await supabase
                .from('job_posts')
                .select(`
          id, title, date, time, region, district, location, status, duration_type
        `)
                .eq('status', 'open')
                .order('date', { ascending: true })

            if (jobsError) {
                console.error('loadOpenJobs error', jobsError)
                setJobs([])
            } else {
                setJobs(data || [])
            }
        } catch (e) {
            console.error('loadOpenJobs exception', e)
            setJobs([])
        } finally {
            setJobsLoading(false)
        }
    }, [])

    useEffect(() => {
        loadOpenJobs()
    }, [loadOpenJobs])

    // Work Type Mapping Helper
    const getWorkTypeCategory = (job) => {
        const d = job.duration_type || ''
        if (d === '1~3시간' || d === '반나절 (4~6시간)') return '시간제 알바'
        if (d === '하루' || d === '하루 이상 일주일') return '단기알바'
        if (d === '일주일이상') return '풀타임'
        if (d.includes('파트')) return '파트타임'
        return '기타'
    }

    // Filtered Jobs
    const filteredJobs = useMemo(() => {
        return jobs.filter(job => {
            // Region Filter
            if (selectedRegion && selectedRegion !== '모든 지역') {
                if (job.region !== selectedRegion) return false
                if (selectedDistrict && selectedDistrict !== '전체 (구)') {
                    if (job.district !== selectedDistrict) return false
                }
            }
            // Work Type Filter
            if (selectedWorkType !== '모든 근무 유형') {
                const category = getWorkTypeCategory(job)
                if (category !== selectedWorkType) return false
            }
            return true
        })
    }, [jobs, selectedRegion, selectedDistrict, selectedWorkType])

    // Calendar Helpers
    const daysInMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
    const startDay = (d) => new Date(d.getFullYear(), d.getMonth(), 1).getDay()
    const totalDays = daysInMonth(currentMonth)
    const startingDayIndex = startDay(currentMonth)

    const daysArray = useMemo(() => {
        const arr = []
        for (let i = 0; i < startingDayIndex; i++) arr.push(null)
        for (let i = 1; i <= totalDays; i++) arr.push(i)
        return arr
    }, [startingDayIndex, totalDays])

    const formattedMonth = `${currentMonth.getFullYear()}년 ${currentMonth.getMonth() + 1}월`

    const jobsByDay = useMemo(() => {
        const map = {}
        const y = currentMonth.getFullYear()
        const m = currentMonth.getMonth() + 1
        const pad = (n) => String(n).padStart(2, '0')
        const monthPrefix = `${y}-${pad(m)}-`

        // Use filteredJobs instead of all jobs
        const monthJobs = filteredJobs.filter((j) => j.date?.startsWith(monthPrefix))

        monthJobs.forEach((j) => {
            const day = Number(j.date.slice(-2))
            if (!map[day]) map[day] = []
            map[day].push(j)
        })
        return map
    }, [filteredJobs, currentMonth])

    const goToNextMonth = () => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
    const goToPrevMonth = () => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))

    return (
        <div className="flex flex-col h-full bg-white">
            <section className="p-4 sm:p-5 flex-1">
                {/* 
                   Updated Header with Filters 
                   - Replaced "이번 달 모집 일정" with Filters
                */}
                <div className="flex flex-col gap-4 mb-4">
                    {/* Filters Row */}
                    {/* Filters Row */}
                    <div className="flex gap-2">
                        {/* Region Filter - Trigger Modal */}
                        <button
                            onClick={() => setIsRegionModalOpen(true)}
                            className={`flex-1 flex items-center justify-between border rounded-lg pl-3 pr-2 py-2.5 text-sm font-semibold shadow-sm transition
                                ${selectedRegion ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-slate-200 text-slate-700 bg-white'}
                            `}
                        >
                            <span className="truncate">
                                {selectedRegion
                                    ? `${selectedRegion} ${selectedDistrict && selectedDistrict !== '전체 (구)' ? selectedDistrict : ''}`
                                    : '모든 지역'}
                            </span>
                            <svg className="w-4 h-4 text-slate-400 flex-none ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </button>

                        <RegionPickerModal
                            isOpen={isRegionModalOpen}
                            onClose={() => setIsRegionModalOpen(false)}
                            onApply={(r, d) => {
                                setSelectedRegion(r)
                                setSelectedDistrict(d)
                            }}
                            initialRegion={selectedRegion}
                            initialDistrict={selectedDistrict}
                        />

                        {/* Work Type Filter */}
                        <div className="relative">
                            <select
                                value={selectedWorkType}
                                onChange={(e) => setSelectedWorkType(e.target.value)}
                                className="appearance-none border border-slate-200 rounded-lg pl-3 pr-8 py-2.5 text-sm font-semibold text-slate-700 bg-white focus:ring-2 focus:ring-blue-500 outline-none shadow-sm h-full"
                            >
                                <option value="모든 근무 유형">모든 근무 유형</option>
                                <option value="풀타임">풀타임</option>
                                <option value="파트타임">파트타임</option>
                                <option value="시간제 알바">시간제 알바 (1~4시간)</option>
                                <option value="단기알바">단기알바</option>
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-500">
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                            </div>
                        </div>
                    </div>

                    {/* Month Navigator (Moved below filters or inline? Image showed filters at top left. Calendar nav usually top also. ) 
                        User image 5 showed filters at very top. 
                        I'll keep Month Navigator below filters or aligned.
                    */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <button type="button" onClick={goToPrevMonth} className="p-1 px-3 border rounded text-xs text-gray-500">이전</button>
                            <span className="font-bold text-gray-800 text-sm">{formattedMonth}</span>
                            <button type="button" onClick={goToNextMonth} className="p-1 px-3 border rounded text-xs text-gray-500">다음</button>
                        </div>
                        {/* Optional: Filter reset or count? */}
                    </div>
                </div>

                <div className="grid grid-cols-7 text-center font-semibold text-xs text-gray-500 border-b pb-2 mb-2">
                    {['일', '월', '화', '수', '목', '금', '토'].map((d) => (
                        <div key={d} className={d === '일' ? 'text-red-500' : d === '토' ? 'text-blue-500' : ''}>
                            {d}
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-7 text-xs auto-rows-fr">
                    {daysArray.map((day, idx) => {
                        const jobsOnDay = day ? jobsByDay[day] || [] : []
                        const hasJob = jobsOnDay.length > 0
                        return (
                            <div
                                key={idx}
                                className={`min-h-[64px] sm:min-h-[80px] border border-gray-100 p-1 text-left align-top 
                  ${hasJob ? 'bg-blue-50/50' : 'bg-white'} 
                 `}
                            >
                                <div className={`text-[11px] font-semibold ${hasJob ? 'text-blue-700' : 'text-gray-500'}`}>
                                    {day}
                                </div>
                                {hasJob && (
                                    <div className="mt-1">
                                        <span className="inline-flex items-center justify-center bg-blue-500 text-white text-[9px] rounded-full px-1.5 h-4 min-w-[16px] font-bold shadow-sm">
                                            {jobsOnDay.length}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>

                <p className="mt-4 text-xs text-gray-400 text-center">
                    * 날짜를 클릭하여 상세 공고를 확인하세요.
                </p>
            </section>
        </div>
    )
}
